import { GameIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { itemById } from "@zend/content";
import { getLocale, pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { useServerTick } from "../ui/Timer.js";
import { useUnits } from "../units/state.js";
import { mailApi } from "./api.js";
import { useSocial } from "./state.js";
import { MAIL_FILTERS, mailRow, relativeTime, type MailFilter, type MailIcon } from "./mailModel.js";
import type { MailItem } from "./types.js";
import "./mail.css";

function rewardText(m: MailItem, t: ReturnType<typeof useT>): string {
  const r = m.rewards;
  if (!r) return "";
  const parts: string[] = [];
  for (const [k, v] of Object.entries(r.resources ?? {})) parts.push(`${formatCompact(v)} ${t(`res.${k}`)}`);
  for (const i of r.items ?? []) parts.push(`${i.qty}× ${pick(itemById(i.itemId).name)}`);
  if (r.credits) parts.push(`${r.credits} ₵`);
  return parts.join(" · ");
}

const ICON_ART: Record<MailIcon, { art: string; glyph: "check" | "close" | "mail" | null }> = {
  "battle-won": { art: "emote.trophy", glyph: "check" },
  "battle-lost": { art: "emote.skull", glyph: "close" },
  reward: { art: "itm.chest_gold", glyph: null },
  system: { art: "bld.comms_tower", glyph: null },
};

/** The inbox as a compact list: filter chips, one toolbar, rows with outcome icons and previews, actions on open (R72, D66). */
export function MailView() {
  const t = useT();
  const locale = getLocale();
  const now = useServerTick(30);
  const mail = useSocial((s) => s.mail);
  const filter = useSocial((s) => s.mailFilter);
  const trash = useSocial((s) => s.mailTrash);
  const setFilter = useSocial((s) => s.setMailFilter);
  const refresh = useSocial((s) => s.refreshMail);
  const run = useSocial((s) => s.run);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  if (!mail) return <p className="dim" style={{ padding: 12 }}>{t("common.loading")}</p>;

  const openMail = async (m: MailItem) => {
    setOpen(open === m.id ? null : m.id);
    if (!m.read) {
      await mailApi.read(m.id).catch(() => undefined);
      void refresh();
    }
  };
  const claim = async (m: MailItem) => {
    const res = await run(() => mailApi.claim(m.id), t("social.mail.claimed"));
    if (res) {
      useUnits.setState({ lastLoot: res.loot });
      void refresh();
    }
  };
  const claimAll = async () => {
    const res = await run(() => mailApi.claimAll());
    if (res) {
      if (res.claimed > 0) useUnits.setState({ lastLoot: res.loot });
      void refresh();
    }
  };
  const readAll = async () => {
    if (await run(() => mailApi.readAll(), t("social.mail.readAllDone"))) void refresh();
  };
  const deleteRead = async () => {
    const res = await run(() => mailApi.deleteRead());
    if (res) void refresh();
  };
  const togglePin = async (m: MailItem) => {
    if (await run(() => (m.pinned ? mailApi.unpin(m.id) : mailApi.pin(m.id)), t(m.pinned ? "social.mail.unpinned" : "social.mail.pinned"))) void refresh();
  };
  const full = mail.used >= mail.capacity * 0.9;

  return (
    <div className="mail" data-testid="mail-view">
      <div className="mail-toolbar">
        <div className="mail-filters" role="tablist">
          {MAIL_FILTERS.map((f: MailFilter) => (
            <button key={f} type="button" role="tab" aria-selected={!trash && filter === f} className={`mail-filter ${!trash && filter === f ? "active" : ""}`} onClick={() => setFilter(f, false)} data-testid={`mail-filter-${f}`}>
              {t(`social.mail.filter.${f}`)}
            </button>
          ))}
          <button type="button" role="tab" aria-selected={trash} className={`mail-filter ${trash ? "active" : ""}`} onClick={() => setFilter("all", true)} data-testid="mail-filter-trash">
            <FunctionalIcon name="trash" /> {t("social.mail.trash")}
          </button>
        </div>
        <div className="mail-actions">
          <span className={`badge ${full ? "warn" : ""}`} data-testid="mail-capacity" title={t("social.mail.capacityHint", { pinned: mail.pinned })}>
            <FunctionalIcon name="mail" /> {mail.used}/{mail.capacity}
          </span>
          <Button variant="gold" disabled={mail.claimable === 0} onClick={() => void claimAll()} data-testid="mail-claim-all">
            {t("social.mail.claimAll")} ({mail.claimable})
          </Button>
          <Button disabled={mail.unread === 0} onClick={() => void readAll()} data-testid="mail-read-all">
            <FunctionalIcon name="check" /> {t("social.mail.readAll")}
          </Button>
          <Button onClick={() => void deleteRead()} data-testid="mail-delete-read">
            <FunctionalIcon name="trash" /> {t("social.mail.deleteRead")}
          </Button>
        </div>
      </div>
      {mail.mail.length === 0 ? <p className="dim" style={{ padding: 12 }}>{t("social.mail.empty")}</p> : null}
      {mail.mail.map((m) => {
        const row = mailRow(m, locale);
        const art = ICON_ART[row.icon];
        const expanded = open === m.id;
        return (
          <article key={m.id} className={`mail-row ${m.read ? "" : "unread"} ${expanded ? "open" : ""}`} data-testid={`mail-${m.id}`} data-icon={row.icon} data-pinned={m.pinned ? "true" : undefined}>
            <button type="button" className="plain mail-heading" onClick={() => void openMail(m)} aria-expanded={expanded} data-testid="mail-open">
              <span className={`mail-icon ${row.icon}`} aria-hidden>
                <GameIcon id={art.art} size={40} />
                {art.glyph ? <FunctionalIcon name={art.glyph} size={12} /> : null}
              </span>
              <span className="mail-copy">
                <span className="mail-title" data-testid="mail-title">{row.title}</span>
                <span className="mail-preview" data-testid="mail-preview">{row.preview}</span>
              </span>
              <span className="mail-meta">
                <time dateTime={new Date(m.createdAt).toISOString()}>{relativeTime(m.createdAt, now, locale)}</time>
                {row.claimable ? <span className="badge gold" data-testid="mail-claimable"><GameIcon id="itm.chest_gold" size={16} /></span> : null}
                {m.pinned ? <FunctionalIcon name="bookmark" size={14} /> : null}
                {!m.read ? <span className="mail-dot" /> : null}
              </span>
            </button>
            <button type="button" className={`plain mail-pin ${m.pinned ? "on" : ""}`} onClick={() => void togglePin(m)} aria-label={t(m.pinned ? "social.mail.unpin" : "social.mail.pin")} aria-pressed={m.pinned} data-testid="mail-pin">
              <FunctionalIcon name="bookmark" size={16} />
            </button>
            {expanded ? (
              <div className="mail-body">
                <p>{m.body.split("\n").map((line, i) => <span key={i}>{line.includes(" / ") ? (locale === "de" ? line.split(" / ")[0] : line.split(" / ").slice(1).join(" / ")) : line}<br /></span>)}</p>
                {m.rewards ? <div className="hint"><GameIcon id="itm.chest_gold" size={18} /> {rewardText(m, t)}</div> : null}
                <div className="mail-row-actions">
                  {row.claimable && !m.deletedAt ? (
                    <Button variant="gold" onClick={() => void claim(m)} data-testid="mail-claim">{t("social.mail.claim")}</Button>
                  ) : null}
                  {m.rewards && m.claimed ? <span className="badge ok"><FunctionalIcon name="check" /> {t("social.mail.claimedBadge")}</span> : null}
                  {row.watch ? (
                    <Button variant="primary" onClick={() => navigate(`/app/battle/${m.ref}`)} data-testid="mail-report"><FunctionalIcon name="play" /> {t("social.mail.watch")}</Button>
                  ) : null}
                  {m.deletedAt ? (
                    <Button onClick={() => void run(() => mailApi.restore(m.id)).then(() => refresh())} data-testid="mail-restore"><FunctionalIcon name="undo" /> {t("social.mail.restore")}</Button>
                  ) : (
                    <Button onClick={() => void run(() => mailApi.remove(m.id)).then(() => refresh())} data-testid="mail-delete"><FunctionalIcon name="trash" /> {t("social.mail.delete")}</Button>
                  )}
                </div>
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
